import { useEffect, useState, useMemo } from 'react';

// Site degrades visually based on total time spent as a user
const SiteDegradation = () => {
  const [decayFactor, setDecayFactor] = useState(0);
  const [glitchActive, setGlitchActive] = useState(false);
  const [noiseOpacity, setNoiseOpacity] = useState(0);

  useEffect(() => {
    try {
      // Calculate decay based on total visits and time
      const totalVisits = parseInt(localStorage.getItem('total-visits') || '0');
      const firstVisit = localStorage.getItem('first-visit-time');
      const now = Date.now();
      
      if (!firstVisit) {
        localStorage.setItem('first-visit-time', now.toString());
      }
      
      const daysSinceFirst = firstVisit 
        ? (now - parseInt(firstVisit)) / (1000 * 60 * 60 * 24)
        : 0;
      
      // Decay increases with visits and time (max 1.0)
      const visitDecay = Math.min(totalVisits / 100, 0.5);
      const timeDecay = Math.min(daysSinceFirst / 30, 0.5);
      const totalDecay = Math.min(visitDecay + timeDecay, 1);
      
      setDecayFactor(totalDecay);
      setNoiseOpacity(totalDecay * 0.03);
      
      // Update visit count
      localStorage.setItem('total-visits', (totalVisits + 1).toString());
    } catch (e) {
      console.error('Degradation storage error:', e);
    }
  }, []);

  // Random glitches based on decay
  useEffect(() => {
    if (decayFactor < 0.2) return;
    
    const interval = setInterval(() => {
      if (Math.random() < decayFactor * 0.15) {
        setGlitchActive(true);
        setTimeout(() => setGlitchActive(false), 50 + Math.random() * 150);
      }
    }, 4000 + Math.random() * 4000);
    
    return () => clearInterval(interval);
  }, [decayFactor]);

  // Apply global CSS filter effect
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--site-decay-saturation', String(1 - decayFactor * 0.2));
    root.style.setProperty('--site-decay-contrast', String(1 - decayFactor * 0.05));
    root.style.setProperty('--site-decay-opacity', String(1 - decayFactor * 0.1));
    
    return () => {
      root.style.removeProperty('--site-decay-saturation');
      root.style.removeProperty('--site-decay-contrast');
      root.style.removeProperty('--site-decay-opacity');
    };
  }, [decayFactor]);

  if (decayFactor === 0 && !glitchActive) return null;

  return (
    <>
      {/* Noise overlay */}
      {noiseOpacity > 0 && (
        <div 
          className="fixed inset-0 pointer-events-none z-[9999]"
          style={{
            opacity: noiseOpacity,
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          }}
        />
      )}
      
      {/* Scan lines for heavy decay */}
      {decayFactor > 0.5 && (
        <div 
          className="fixed inset-0 pointer-events-none z-[9999]"
          style={{
            opacity: (decayFactor - 0.5) * 0.3,
            backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.1) 2px, rgba(0,0,0,0.1) 4px)',
          }}
        />
      )}
      
      {/* Glitch overlay */}
      {glitchActive && (
        <div 
          className="fixed inset-0 pointer-events-none z-[9999]"
          style={{
            background: `linear-gradient(${Math.random() * 360}deg, transparent 40%, rgba(255,255,255,0.02) 50%, transparent 60%)`,
          }}
        />
      )}
    </>
  );
};

export default SiteDegradation;
